import { useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import {
    type UseQueryResult,
    useMutation,
    useQuery
} from 'react-query'

import * as spriteIcons from 'assets/svg/sprite'
import { addToast } from 'components/Toast/Toast'
import { ActionCard } from 'components-ref/molecules/ActionCard/ActionCard'
import { AllIcons } from 'helpers/allIcons'
import { Icons } from 'helpers/icons'
import {
    getNotificationSettings,
    putNotifications
} from 'services/endpoints/settings/notifications/notificationSettings'
import { useUserProfile } from 'store'
import { useNotificationSettingsStore } from 'store/notificationSettingsCacheStore'
import { type iNotificationsSettings } from 'types/iTypes'

import '../_settings.scss'

interface RenderSettingsCardsProps {
    type: string
    hasBottomSettings?: boolean
    searchTerm: string
}

export const RenderSettingsCards = ({
    type,
    hasBottomSettings,
    searchTerm
}: RenderSettingsCardsProps) => {
    interface SingleCard {
        icon?: JSX.Element | JSX.Element[] | string
        body: string
        subtext?: string
        link?: string
        absfix?: boolean
        hasArrow?: boolean
        roles: string[]
        fanOrder?: number
        modelOrder?: number
        info?: string | JSX.Element | JSX.Element[]
        hasToggle?: boolean
        toggleActive?: boolean
        toggleFn?: () => void
        searchOnly?: boolean
    }

    interface CardsObject {
        general: {
            top: SingleCard[]
            bottom: SingleCard[] | []
        }
        account: {
            top: SingleCard[]
            bottom: SingleCard[] | []
        }
        notifications: {
            top: SingleCard[]
            bottom: SingleCard[] | []
        }
    }

    const userData = useUserProfile()!
    const filterContainerRef = useRef<any>()
    const { t } = useTranslation()

    const [searchResultsEmpty, setSearchResultsEmpty] = useState(false)

    const notificationSettings = useNotificationSettingsStore(
        state => state.settings
    )

    const setNotificationSettings = useNotificationSettingsStore(
        state => state.setSettings
    )

    const setGlobalNotificationSetting = useNotificationSettingsStore(
        state => state.setGlobalSetting
    )

    const globalNotificationSettings =
        notificationSettings?.notificationSettings

    const shouldFetchNotificationSettings =
    !notificationSettings &&
    (type === 'general' || type === 'notifications')

    const {
        data: notificationData,
        error: notificationError
    }: UseQueryResult<iNotificationsSettings> = useQuery(
        'globalNotificationSettings',
        getNotificationSettings,
        {
            enabled: shouldFetchNotificationSettings,
            refetchOnWindowFocus: false
        }
    )

    useEffect(() => {
        if (notificationData) {
            setNotificationSettings(notificationData)
        }

        if (notificationError) {
            addToast('error', t('error:errorSomethingWentWrong'))
        }
    }, [
        notificationData,
        notificationError,
        setNotificationSettings,
        t
    ])

    const updateNotificationsPreferences = useMutation(
        async (data: Record<string, boolean>) => {
            return await putNotifications(data)
        },
        {
            onError: err => {
                console.error(err)
                addToast('error', t('error:errorSomethingWentWrong'))
            }
        }
    )

    const allSettingsCards: CardsObject = {
        general: {
            top: [
                {
                    icon: <spriteIcons.IconDollarCircleArrows />,
                    body: t('settings:subscriptionSettings'),
                    link: '/settings/general/subscription-options',
                    hasArrow: true,
                    absfix: true,
                    roles: ['model'],
                    modelOrder: 1
                },
                {
                    icon: <spriteIcons.IconWallet />,
                    body: t('settings:payoutSettings'),
                    link: '/settings/general/payout-settings',
                    hasArrow: true,
                    absfix: true,
                    roles: ['model'],
                    modelOrder: 2
                },
                {
                    icon: <spriteIcons.IconNotePencil />,
                    body: t('settings:postSettings'),
                    link: '/settings/general/post-settings',
                    hasArrow: true,
                    absfix: true,
                    roles: ['model'],
                    modelOrder: 3
                },
                {
                    icon: <spriteIcons.IconManCircleBlue />,
                    body: t('settings:profileSettings'),
                    link: '/settings/general/profile-settings',
                    hasArrow: true,
                    absfix: true,
                    roles: ['model'],
                    modelOrder: 4
                },
                {
                    icon: <spriteIcons.IconChatBubble2 />,
                    body: t('settings:chatSettings'),
                    link: '/settings/general/chat-settings',
                    hasArrow: true,
                    absfix: true,
                    roles: ['model', 'fan'],
                    fanOrder: 3,
                    modelOrder: 5
                },
                {
                    icon: <spriteIcons.IconWallet />,
                    body: t('settings:paymentCards'),
                    link: '/settings/general/wallet',
                    hasArrow: true,
                    absfix: true,
                    roles: ['fan', 'model'],
                    fanOrder: 1,
                    modelOrder: 6
                },
                {
                    icon: <spriteIcons.IconLiveStream />,
                    body: t('settings:liveStreamSettings'),
                    link: '/settings/general/live-stream',
                    hasArrow: true,
                    absfix: true,
                    roles: ['model'],
                    modelOrder: 7
                },
                {
                    icon: <spriteIcons.IconPlusInCircle />,
                    body: t('settings:story'),
                    link: '/settings/general/story-settings',
                    hasArrow: true,
                    absfix: true,
                    roles: ['model'],
                    modelOrder: 8
                },
                {
                    icon: <spriteIcons.IconGlobeMagnified />,
                    body: t('settings:language'),
                    link: '/settings/general/language',
                    hasArrow: true,
                    absfix: true,
                    roles: ['model', 'fan'],
                    fanOrder: 4,
                    modelOrder: 9
                },
                {
                    icon: <spriteIcons.IconCogWheelBlue />,
                    body: t('settings:activity'),
                    link: '/settings/general/activity',
                    hasArrow: true,
                    absfix: true,
                    roles: ['model', 'fan'],
                    fanOrder: 2,
                    modelOrder: 11
                }
            ],
            bottom: []
        },

        account: {
            top: [
                {
                    icon: <spriteIcons.IconManCircleBlue />,
                    body: t('settings:changeDisplayName'),
                    subtext: userData.display_name,
                    link: '/settings/account/change-display-name',
                    hasArrow: true,
                    absfix: true,
                    roles: ['model', 'fan']
                },
                {
                    icon: <spriteIcons.IconManCircleBlue />,
                    body: t('settings:changeUsername'),
                    link: '/settings/account/change-username',
                    hasArrow: true,
                    absfix: true,
                    subtext: userData.username,
                    roles: ['model', 'fan']
                },
                {
                    icon: <spriteIcons.IconLockBlue />,
                    body:
                        userData.has_password === 0
                            ? t('Set Password')
                            : t('settings:changePassword'),
                    link: '/settings/account/change-password',
                    info: '******',
                    hasArrow: true,
                    absfix: true,
                    roles: ['model', 'fan']
                },
                {
                    icon: <spriteIcons.IconMail />,
                    body: t('settings:changeEmail'),
                    subtext: userData.email,
                    link: '/settings/account/change-email',
                    hasArrow: true,
                    roles: ['model', 'fan']
                },
                {
                    icon: <spriteIcons.IconPhoneBlue />,
                    body: t('settings:changePhoneNumber'),
                    link: '/settings/account/change-phone',
                    hasArrow: true,
                    subtext: userData.phone_number
                        ? `*******${userData.phone_number}`
                        : '',
                    absfix: true,
                    roles: ['model', 'fan']
                },
                {
                    icon: <spriteIcons.IconManCircleBlue />,
                    body: t('settings:connectAnotherAccount'),
                    info: t('connectASecondaryAccount'),
                    link: '/settings/account/connect-another-account',
                    hasArrow: true,
                    absfix: true,
                    roles: ['model']
                },
                {
                    icon: <spriteIcons.IconChatBubbles />,
                    body: t('settings:connectSocialMediaAccounts'),
                    info: t('linkWithEmailOrFacebookWillKeep'),
                    link: '/settings/account/connect-social-accounts',
                    hasArrow: true,
                    absfix: true,
                    roles: ['model', 'fan']
                }
            ],

            bottom: [
                {
                    icon: <spriteIcons.IconPrivacyShield />,
                    body: t('settings:privacySecurity'),
                    link: '/settings/account/privacy-and-security',
                    hasArrow: true,
                    roles: ['model', 'fan']
                },
                {
                    icon: <spriteIcons.IconManLineThrough />,
                    body: t('blockedUsers'),
                    link: '/settings/account/privacy-and-security/blocked-users',
                    hasArrow: true,
                    absfix: true,
                    roles: ['model', 'fan'],
                    searchOnly: true
                },
                {
                    icon: <spriteIcons.IconManLineThrough />,
                    body: t('restrictedUsers'),
                    link: '/settings/account/privacy-and-security/restricted-users',
                    hasArrow: true,
                    absfix: true,
                    roles: ['model'],
                    searchOnly: true
                },
                {
                    icon: <spriteIcons.IconGPSPoint />,
                    body: t('geoBlocking'),
                    link: '/settings/account/privacy-and-security/geo-blocking',
                    hasArrow: true,
                    absfix: true,
                    roles: ['model'],
                    searchOnly: true
                },
                {
                    icon: <spriteIcons.IconMagnifyingGlassChecked />,
                    body: t('discoverySettings'),
                    link: '/settings/account/privacy-and-security/discovery-settings',
                    hasArrow: true,
                    absfix: true,
                    roles: ['model'],
                    searchOnly: true
                },
                {
                    icon: <spriteIcons.IconMagnifyingGlassChecked />,
                    body: t('watermarkSettings'),
                    link: '/settings/account/privacy-and-security/watermark-settings',
                    hasArrow: true,
                    absfix: true,
                    roles: ['model'],
                    searchOnly: true
                },
                {
                    icon: <spriteIcons.IconScanSquare />,
                    body: t('twoFactorAuthentication'),
                    link: '/settings/account/privacy-and-security/two-step-verification',
                    hasArrow: true,
                    absfix: true,
                    roles: ['model', 'fan'],
                    searchOnly: true
                },
                {
                    icon: <spriteIcons.IconNote />,
                    body: t('loginSessions'),
                    link: '/settings/account/privacy-and-security/login-sessions',
                    hasArrow: true,
                    absfix: true,
                    roles: ['model', 'fan'],
                    searchOnly: true
                }
            ]
        },

        notifications: {
            top: [
                                {
                    icon: AllIcons.settings_notifications_inapp,
                    body: t('settings:inAppNotifications'),
                    link: '/settings/notifications/in-app-notifications',
                    hasArrow: true,
                    absfix: true,
                    hasToggle: !!globalNotificationSettings,
                    toggleActive:
                        globalNotificationSettings?.in_app_notifications_enabled,
                    toggleFn: () => {
                        if (!globalNotificationSettings) return

                        const value =
                            !globalNotificationSettings.in_app_notifications_enabled

                        setGlobalNotificationSetting(
                            'in_app_notifications_enabled',
                            value
                        )

                        updateNotificationsPreferences.mutate({
                            in_app_notifications_enabled: value
                        })
                    },
                    roles: ['model', 'fan']
                },
                {
                    icon: AllIcons.settings_notifications_inapp,
                    body: t('settings:toastNotifications'),
                    link: '/settings/notifications/toast-notifications',
                    hasArrow: true,
                    absfix: true,
                    hasToggle: !!globalNotificationSettings,
                    toggleActive:
                        globalNotificationSettings?.toast_notifications_enabled,
                    toggleFn: () => {
                        if (!globalNotificationSettings) return

                        const value =
                            !globalNotificationSettings.toast_notifications_enabled

                        setGlobalNotificationSetting(
                            'toast_notifications_enabled',
                            value
                        )

                        updateNotificationsPreferences.mutate({
                            toast_notifications_enabled: value
                        })
                    },
                    roles: ['model', 'fan']
                },
                {
                    icon: Icons.mail,
                    body: t('settings:emailNotifications'),
                    link: '/settings/notifications/email-notifications',
                    hasArrow: true,
                    hasToggle: !!globalNotificationSettings,
                    toggleActive:
                        globalNotificationSettings?.email_notifications_enabled,
                    toggleFn: () => {
                        if (!globalNotificationSettings) return

                        const value =
                            !globalNotificationSettings.email_notifications_enabled

                        setGlobalNotificationSetting(
                            'email_notifications_enabled',
                            value
                        )

                        updateNotificationsPreferences.mutate({
                            email_notifications_enabled: value
                        })
                    },
                    roles: ['model', 'fan']
                },
                {
                    icon: Icons.notification,
                    body: t('settings:pushNotifications'),
                    link: '/settings/notifications/push-notifications',
                    absfix: true,
                    hasArrow: true,
                    hasToggle: !!globalNotificationSettings,
                    toggleActive:
                        globalNotificationSettings?.push_notifications_enabled,
                    toggleFn: () => {
                        if (!globalNotificationSettings) return

                        const value =
                            !globalNotificationSettings.push_notifications_enabled

                        setGlobalNotificationSetting(
                            'push_notifications_enabled',
                            value
                        )

                        updateNotificationsPreferences.mutate({
                            push_notifications_enabled: value
                        })
                    },
                    roles: ['model', 'fan']
                },
/*                 {
                    icon: Icons.mobile,
                    body: t('settings:smsNotifications'),
                    link: '/settings/notifications/sms-notifications',
                    absfix: true,
                    hasArrow: true,
                    hasToggle: !!globalNotificationSettings,
                    toggleActive:
                        globalNotificationSettings?.sms_notifications_enabled,
                    toggleFn: () => {
                        if (!globalNotificationSettings) return

                        const value =
                            !globalNotificationSettings.sms_notifications_enabled

                        setGlobalNotificationSetting(
                            'sms_notifications_enabled',
                            value
                        )

                        updateNotificationsPreferences.mutate({
                            sms_notifications_enabled: value
                        })
                    },
                    roles: ['model', 'fan']
                }, */
            ],

            bottom: []
        }
    }

    const renderTopCards = () => {
        const role = userData.role

        const cards = allSettingsCards[
            type as keyof typeof allSettingsCards
        ].top
            .filter(card => card.roles.includes(role))
            .map((card, originalIndex) => ({
                card,
                originalIndex
            }))
            .sort((a, b) => {
                const aOrder =
                    role === 'model'
                        ? a.card.modelOrder
                        : a.card.fanOrder

                const bOrder =
                    role === 'model'
                        ? b.card.modelOrder
                        : b.card.fanOrder

                const normalizedA =
                    aOrder ?? Number.MAX_SAFE_INTEGER

                const normalizedB =
                    bOrder ?? Number.MAX_SAFE_INTEGER

                if (normalizedA !== normalizedB) {
                    return normalizedA - normalizedB
                }

                return a.originalIndex - b.originalIndex
            })

        return cards.map(({ card, originalIndex }) => (
            <div
                key={`${card.link ?? card.body}-${originalIndex}`}
            >
                <ActionCard
                    icon={card.icon}
                    iconSize='small'
                    text={card.body}
                    subtext={card.subtext}
                    link={card.link}
                    absFix={!!card.absfix}
                    hasArrow={!!card.hasArrow}
                    description={card.info}
                    hasSwitch={card.hasToggle}
                    toggleActive={card.toggleActive}
                    toggleFn={card.toggleFn}
                />
            </div>
        ))
    }

    const renderBottomCards = () => {
        if (
            hasBottomSettings &&
            allSettingsCards[
                type as keyof typeof allSettingsCards
            ].bottom.length > 0
        ) {
            return allSettingsCards[
                type as keyof typeof allSettingsCards
            ].bottom
                ?.filter(card => !card.searchOnly)
                .map(
                (
                    {
                        icon,
                        body,
                        subtext,
                        link,
                        absfix,
                        hasArrow,
                        roles,
                        fanOrder,
                        modelOrder,
                        info,
                        toggleActive,
                        toggleFn,
                        hasToggle
                    }: SingleCard,
                    index
                ) => {
                    if (roles.includes(userData.role)) {
                        return (
                            <div
                                key={index}
                                style={
                                    userData.role === 'model'
                                        ? modelOrder
                                            ? {
                                                  order: modelOrder
                                              }
                                            : {
                                                  order: 'initial'
                                              }
                                        : fanOrder
                                        ? {
                                              order: fanOrder
                                          }
                                        : {
                                              order: 'initial'
                                          }
                                }
                            >
                                <ActionCard
                                    icon={icon}
                                    iconSize='small'
                                    text={body}
                                    subtext={subtext}
                                    link={link}
                                    absFix={!!absfix}
                                    hasArrow={!!hasArrow}
                                    description={info}
                                    hasSwitch={hasToggle}
                                    toggleActive={toggleActive}
                                    toggleFn={toggleFn}
                                />
                            </div>
                        )
                    }

                    return null
                }
            )
        }

        return null
    }

    const renderFilteredCards = (
        type: string,
        title: string
    ) => {
        const filterArray = {
            general: [
                ...allSettingsCards.general.top,
                ...allSettingsCards.general.bottom
            ].filter(
                (item: any) =>
                    item.body
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()) &&
                    item.roles.includes(userData.role)
            ),

            account: [
                ...allSettingsCards.account.top,
                ...allSettingsCards.account.bottom
            ].filter(
                (item: any) =>
                    item.body
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()) &&
                    item.roles.includes(userData.role)
            ),

            notifications: [
                ...allSettingsCards.notifications.top,
                ...allSettingsCards.notifications.bottom
            ].filter(
                (item: any) =>
                    item.body
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()) &&
                    item.roles.includes(userData.role)
            )
        }

        if (
            filterArray[
                type as keyof typeof filterArray
            ].length > 0
        ) {
            return (
                <div className='settings__filtered'>
                    <h2>{title}</h2>

                    {filterArray[
                        type as keyof typeof filterArray
                    ].map(
                        (
                            {
                                icon,
                                body,
                                subtext,
                                link,
                                absfix,
                                hasArrow,
                                roles,
                                info,
                                toggleActive,
                                toggleFn,
                                hasToggle
                            }: SingleCard,
                            index
                        ) => {
                            if (
                                roles.includes(userData.role)
                            ) {
                                return (
                                    <ActionCard
                                        key={index}
                                        icon={icon}
                                        iconSize='small'
                                        text={body}
                                        subtext={subtext}
                                        link={link}
                                        absFix={!!absfix}
                                        hasArrow={
                                            !!hasArrow
                                        }
                                        description={info}
                                        hasSwitch={
                                            hasToggle
                                        }
                                        toggleActive={
                                            toggleActive
                                        }
                                        toggleFn={
                                            toggleFn
                                        }
                                    />
                                )
                            }

                            return null
                        }
                    )}
                </div>
            )
        }

        return null
    }

    useEffect(() => {
        if (filterContainerRef.current) {
            if (
                filterContainerRef.current.children
                    .length === 0
            ) {
                setSearchResultsEmpty(true)
            } else {
                setSearchResultsEmpty(false)
            }
        }
    }, [filterContainerRef, searchTerm])

    return searchTerm.trim() === '' ? (
        <div className='settings settings__fields'>
            {renderTopCards()}

            {hasBottomSettings ? (
                <hr className='settings__fields__break' />
            ) : (
                ''
            )}

            {hasBottomSettings
                ? renderBottomCards()
                : ''}
        </div>
    ) : (
        <>
            <div ref={filterContainerRef}>
                {renderFilteredCards(
                    'general',
                    t('settings:generalSettings')
                )}

                {renderFilteredCards(
                    'account',
                    t('settings:accountSettings')
                )}

                {renderFilteredCards(
                    'notifications',
                    t('settings:notificationSettings')
                )}
            </div>

            {searchResultsEmpty && (
                <div className='settings-search-no-results'>
                    {t('noResults')}
                </div>
            )}
        </>
    )
}